import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Badge, IconButton, Tooltip, Typography } from '@material-ui/core';
import TuneIcon from '@material-ui/icons/Tune';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../store/index';
import DatePicker from './DatePicker';
import CustomDatePicker from './CustomDatePicker';

const useStyles = makeStyles((theme) => ({
  rootDrawer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '4px',
  },

  rootNavbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginRight: '12px',
    flexShrink: 0,
  },

  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  },

  tooltipText: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  toolbarIconButton: {
    padding: 4,
  },

  toolbarIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
  },

  navIconButton: {
    padding: '2px',
    '&:hover': {
      backgroundColor: theme.palette.accents.dark,
    },
  },

  navIcon: {
    color: theme.palette.accents.main,
    '&:hover': {
      color: 'white',
    },
  },
}));

export default function ExamListDateFilterControls({
  filterIsActive = false,
  extraActions = null,
  variant = 'drawer',
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const dates = useSelector((state) => state.dates);
  const { landscape } = useSelector((state) => state.gui);
  const isNavbar = variant === 'navbar';

  const setDates = (nextDates) => dispatch(actions.setFilterProperty(['dates', nextDates]));
  const setCustomDate = (date) => dispatch(actions.setCustomDate(date));
  const setSelectedRange = (range) => dispatch(actions.setSelectedRange(range));

  const filterButton = (
    <Tooltip
      arrow
      placement={isNavbar ? 'bottom-end' : 'bottom'}
      title={
        <Typography color='inherit' className={classes.tooltipText}>
          {t('Menu.filter')}
        </Typography>
      }>
      <Badge
        color={isNavbar ? 'primary' : 'secondary'}
        overlap={isNavbar ? 'rectangular' : 'circular'}
        variant='dot'
        invisible={!filterIsActive}
        data-test-id='patient-list-filter'
        style={isNavbar ? { margin: '0 4px' } : undefined}>
        <IconButton
          size={isNavbar && landscape ? 'medium' : 'small'}
          className={isNavbar ? classes.navIconButton : classes.toolbarIconButton}
          onClick={() => dispatch(actions.setActiveDialog('filter'))}>
          <TuneIcon
            className={isNavbar ? classes.navIcon : classes.toolbarIcon}
            style={isNavbar && !landscape ? { maxHeight: '18px', maxWidth: '18px' } : undefined}
          />
        </IconButton>
      </Badge>
    </Tooltip>
  );

  const datePicker = (
    <DatePicker
      setDates={setDates}
      customDate={dates.customDate}
      setCustomDate={setCustomDate}
      selectedRange={dates.selectedRange}
      setSelectedRange={setSelectedRange}
    />
  );

  const customDatePicker =
    dates.selectedRange === 'custom' ? (
      <CustomDatePicker setDates={setDates} customDate={dates.customDate} setCustomDate={setCustomDate} />
    ) : null;

  if (isNavbar) {
    return (
      <div className={classes.rootNavbar}>
        {datePicker}
        {customDatePicker}
        {filterButton}
      </div>
    );
  }

  return (
    <div className={classes.rootDrawer}>
      <div className={classes.row}>
        {datePicker}
        <div className={classes.actions}>
          {filterButton}
          {extraActions}
        </div>
      </div>
      {customDatePicker}
    </div>
  );
}
